import { useCallback, useMemo, useState } from "react";

interface UseSortOrderOptions<T, K extends string> {
	items: T[];
	initialOrder?: K;
	sortFn: (items: T[], order: K) => T[];
}

interface UseSortOrderReturn<T, K extends string> {
	sortOrder: K;
	sortedItems: T[];
	handleSortChange: (_event: React.MouseEvent<HTMLElement>, newOrder: K | null) => void;
	setSortOrder: (order: K) => void;
}

export const useSortOrder = <T, K extends string>({
	items,
	initialOrder,
	sortFn,
}: UseSortOrderOptions<T, K>): UseSortOrderReturn<T, K> => {
	const [sortOrder, setSortOrder] = useState<K>(initialOrder as K);

	const sortedItems = useMemo(() => {
		return sortFn(items, sortOrder);
	}, [items, sortOrder, sortFn]);

	const handleSortChange = useCallback(
		(_event: React.MouseEvent<HTMLElement>, newOrder: K | null) => {
			if (newOrder !== null) {
				setSortOrder(newOrder);
			}
		},
		[],
	);

	return {
		sortOrder,
		sortedItems,
		handleSortChange,
		setSortOrder,
	};
};
